<template>
	<div class="app-input-wrapper">
		<input
			v-bind="cleanAttrs"
			class="app-input"
			:value="modelValue"
			:class="{
				[`${inputClass}`]: inputClass,
				'xs:pl-14 md:pl-15 pl-12': hasPrependSlot,
				'xs:pr-14 md:pr-15 pr-12': hasAppendSlot,
				'border-error bg-error/10': isInputError,
			}"
			@input="onInput"
			@blur="validate"
			@keydown.enter="onKeypressEnter"
		/>

		<div
			class="top-4.5 xs:left-8 md:w-4.5 md:h-4.5 fill-main-400 absolute left-6 h-4 w-4 lg:top-5"
		>
			<slot name="prepend"></slot>
		</div>

		<div
			class="top-4.5 xs:right-8 md:w-4.5 md:h-4.5 fill-main-400 absolute right-6 h-4 w-4 lg:top-5"
		>
			<slot name="append"></slot>
		</div>

		<div
			v-if="isInputValid || isInputError"
			class="top-4.5 xs:right-8 md:w-4.5 md:h-4.5 fill-main-400 absolute right-6 h-4 w-4 lg:top-5"
		>
			<SuccessSvg
				v-if="isInputValid === true"
				class="md:w-4.5 md:h-4.5 fill-success-base h-4 w-4"
			/>
			<ErrorSvg v-if="isInputError" class="md:w-4.5 md:h-4.5 fill-error h-4 w-4" />
		</div>
		<div v-if="isInputError" class="text-error text-12 absolute left-0 -bottom-4 pl-4 pt-px">
			{{ errorMessage }}
		</div>
	</div>
</template>

<script lang="ts" setup>
	import SuccessSvg from '../VehicleForm/components/icons/CheckSecondSvg.vue'
	import ErrorSvg from '../VehicleForm/components/icons/ErrorSvg.vue'
	import { phoneMaskHelper } from '../../services/helpers'
	import { useFormStore } from '../../stores/form'
	import { computed, useSlots, ref, useAttrs } from 'vue'
	import { AppInputRule } from './types'

	// props
	const props = withDefaults(
		defineProps<{
			inputClass?: string
			modelValue?: string
			isPhone?: boolean
			rules?: AppInputRule[]
		}>(),
		{},
	)

	// emits
	const emit = defineEmits(['update:modelValue', 'submit'])

	// hooks
	const attrs = useAttrs()
	const slots = useSlots()
	const form = useFormStore()

	// refs
	const isInputValid = ref(false)
	const isInputError = ref(false)
	const errorMessage = ref('')

	// computed
	const hasPrependSlot = computed(() => !!slots['prepend'])
	const hasAppendSlot = computed(() => !!slots['append'])
	const cleanAttrs = computed(() => {
		return { ...attrs, class: undefined }
	})

	// methods
	const onInput = (event: Event) => {
		const target = event.target as HTMLInputElement
		if (props.isPhone) {
			phoneMaskHelper(target.value, target)
		}
		target && emit('update:modelValue', target.value)
	}

	const onKeypressEnter = (e: KeyboardEvent) => {
		const target = e.target as HTMLInputElement
		target?.setCustomValidity(' ')
		target?.blur()
		emit('submit')
	}

	const validate = () => {
		errorMessage.value = ''
		isInputError.value = false
		isInputValid.value = false

		if (!props.rules?.length) return

		for (let rule of props.rules) {
			const result = rule(props.modelValue)

			if (!result || typeof result === 'string') {
				errorMessage.value = result || ''
				isInputError.value = true
				return
			}
		}

		isInputValid.value = true
	}

	defineExpose({
		validate,
	})
</script>
